import { RefObject } from 'react';
import useClickAwayListener from '@/utils/use-click-away-listener';

export type UsePopoverClickAwayParams = {
  containerRef: RefObject<HTMLDivElement | null>;
  portalEl?: RefObject<HTMLDivElement | null>;
  open: boolean;
  closeOnClick: boolean;
  closeOnEscape: boolean;
  closeOnTabAway: boolean;
  ignoreClasses: string[];
  ignoreClickAway?: boolean;
  multiClickAway?: boolean;
  handleClose: () => void;
  onPointerUpOutside?: (e: MouseEvent) => void;
  onEscape?: () => void;
};

/**
 * Wraps useClickAwayListener with popover-specific logic.
 * Handles portal-aware click handling with class ignoring.
 */
export function usePopoverClickAway({
  containerRef,
  portalEl,
  open,
  closeOnClick,
  closeOnEscape,
  closeOnTabAway,
  ignoreClasses,
  ignoreClickAway,
  multiClickAway,
  handleClose,
  onPointerUpOutside,
  onEscape,
}: UsePopoverClickAwayParams): void {
  useClickAwayListener({
    ignoreClasses,
    ref: containerRef,
    open,
    onClickAway: (e) => {
      if (multiClickAway) {
        // if portalEl and the click is inside, return early
        if (portalEl?.current?.contains(e.target as Node)) {
          if (closeOnClick) {
            handleClose();
          }
          return;
        }
        handleClose();
      }
    },
    onPointerUpOutside: (e) => {
      onPointerUpOutside?.(e);
      if (!open || ignoreClickAway) return;
      if (ignoreClasses.length > 0 && e.target instanceof Element && e.target.closest(ignoreClasses.join(', '))) {
        return;
      }
      // if portalEl and the click is inside, return early
      if (portalEl?.current) {
        if (portalEl.current.contains(e.target as Node)) {
          if (closeOnClick) {
            handleClose();
          }
          return;
        }
      }
      handleClose();
    },
    onEscape: () => {
      onEscape?.();
      if (closeOnEscape) handleClose();
    },
    onTabAway: () => closeOnTabAway && handleClose(),
    onPointerUpInside: (e) => {
      if (ignoreClasses.length > 0 && e.target instanceof Element && e.target.closest(ignoreClasses.join(', '))) {
        return;
      }
      if (closeOnClick) handleClose();
    },
  });
}
